import { rowIdKey } from "./compareData";

const DIGIT_ID = /^\d+$/;

/** API / grid ids: positive int, including numeric strings from JSON. */
export function toPositiveIntId(v: any): number | null {
  if (typeof v === "number" && v > 0) return v;
  if (typeof v === "string" && DIGIT_ID.test(v.trim())) {
    const n = parseInt(v, 10);
    if (n > 0) return n;
  }
  return null;
}

export function parentAssetId(
  t: { asset_id?: any; asset?: { id?: any } } | any,
): number | null {
  const id = t?.asset_id ?? t?.asset?.id;
  return toPositiveIntId(id);
}

/** Strips UI-only fields so getChangedRecords matches API snapshots. */
export function forTenantDiff(list: any[]): any[] {
  return list.map((t) => {
    if (!t || typeof t !== "object") return t;
    const {
      shouldCreate,
      included,
      shouldLink,
      prev_asset_id,
      prev_asset,
      ...rest
    } = t;
    return rest;
  });
}

export function forAssetDiff(list: any[]): any[] {
  return list.map((a) => {
    if (!a || typeof a !== "object") return a;
    // Omit nested tenants — tenant edits live on `tenants` context; nested copies should not mark every asset changed.
    const { shouldCreate: _shouldCreate, is_existing, deal, accordion, tenants, ...rest } = a;
    return {
      ...rest,
      deal_id: rest.deal_id ?? (deal as any)?.id ?? null,
      accordion_id: rest.accordion_id ?? (accordion as any)?.id ?? null,
    };
  });
}

/**
 * Which persisted assets need a single PUT (tenant and/or field changes, or
 * tenant row removals that omit DB ids from the final payload).
 */
export function getAssetIdsToResync(options: {
  changedAssetList: any[];
  changedTenantList: any[];
  tenants: any[];
  originalTenants: any[];
  removedTenants: any[];
  removedDetachingAssetIds: Set<number>;
}): Set<number> {
  const {
    changedAssetList,
    changedTenantList,
    tenants,
    originalTenants,
    removedTenants,
    removedDetachingAssetIds,
  } = options;
  const ids = new Set<number>();
  for (const a of changedAssetList) {
    if (typeof a.id === "number" && a.id > 0) ids.add(a.id);
  }
  for (const t of changedTenantList) {
    const aid = parentAssetId(t);
    if (aid != null) ids.add(aid);
  }
  const origKeySet = new Set(
    originalTenants
      .map((o) => rowIdKey(o.id))
      .filter((k): k is string => k != null && k !== ""),
  );
  for (const t of tenants) {
    if (origKeySet.has(rowIdKey(t.id) ?? "")) continue;
    const aid = parentAssetId(t);
    if (aid != null) ids.add(aid);
  }
  for (const t of removedTenants) {
    const aid = parentAssetId(t);
    if (aid != null && !removedDetachingAssetIds.has(aid)) ids.add(aid);
  }
  return ids;
}
